/**
 * Mounts the guided tours for the Teams app inside the router (RootLayout),
 * wiring the shared engine to who you are (roles), where you are (route) and
 * the server (client.retail.tours → /v3/me/tours).
 */
import { useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { GuidedTourProvider, type TourStore } from '@retail-ops/guided-tour';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { ROP_TOURS } from './ropTours';

export function RopGuidedTours({ ready, children }: { ready: boolean; children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const client = useApiClient();
  const navigate = useNavigate();
  const loc = useLocation();

  const roles = useMemo<ReadonlyArray<string> | null>(() => {
    if (!user) return null;
    const list = (user.roles ?? []).map(String);
    return list.length > 0 ? list : user.primaryRole ? [String(user.primaryRole)] : [];
  }, [user]);

  const store = useMemo<TourStore | null>(() => (client ? {
    load: async () => {
      const r = await client.retail.tours.mine();
      return { enabled: r.data.enabled, progress: r.data.progress };
    },
    record: (tourId, version, status, lastStep) => client.retail.tours.record(tourId, { version, status, lastStep }),
  } : null), [client]);

  return (
    <GuidedTourProvider tours={ROP_TOURS} roles={roles} store={store} navigate={navigate}
      currentPath={loc.pathname} autoStart={ready}>
      {children}
    </GuidedTourProvider>
  );
}

export default RopGuidedTours;
